import React, { useState, useEffect } from 'react';
import { coins, transactions } from '../../utils/api';
import TransactionPromptModal from '../TransactionPromptModal';
import SaleTransactionModal from '../SaleTransactionModal';
import BulkTagEditor from '../BulkTagEditor';
import BulkDocumentManager from '../BulkDocumentManager';

const CoinListBulkActions = ({
  selectedCoins,
  setSelectedCoins,
  onUpdate,
  coinList
}) => {
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [showTransactionPrompt, setShowTransactionPrompt] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showBulkTagEditor, setShowBulkTagEditor] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [possibleStatuses, setPossibleStatuses] = useState([]);
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState(null);
  const [showDocumentManager, setShowDocumentManager] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleSelectAll = () => {
    if (selectedCoins.size === coinList.length) {
      setSelectedCoins(new Set());
    } else {
      setSelectedCoins(new Set(coinList.map(coin => coin.id)));
    }
  };

  const handleBulkDelete = async () => {
    try {
      await coins.bulkDelete(Array.from(selectedCoins));
      onUpdate();
      setSelectedCoins(new Set());
      setShowDeleteWarning(false);
    } catch (err) {
      console.error('Failed to delete coins:', err);
      if (err.response?.data?.error?.includes('transaction')) {
        setError('One or more coins are part of a transaction and cannot be deleted. Please delete the transaction first.');
      } else {
        setError(err.response?.data?.details || 'Failed to delete coins. Please try again later.');
      }
    }
  };

  const handleBulkStatusUpdate = async () => {
    try {
      const statusName = typeof newStatus === 'object' ? newStatus.name : newStatus;
      const isSold = statusName?.toLowerCase() === 'sold';

      if (isSold) {
        setPendingStatusUpdate(statusName);
        setShowTransactionPrompt(true);
        setShowStatusUpdate(false);
        return;
      }

      await coins.bulkUpdateStatus(Array.from(selectedCoins), statusName);
      onUpdate();
      setSelectedCoins(new Set());
      setShowStatusUpdate(false);
    } catch (err) {
      console.error('Failed to update coin statuses:', err);
      setError('Failed to update coin statuses. Please try again later.');
    }
  };

  const handleConfirmTransaction = async (showTransactionForm) => {
    if (showTransactionForm) {
      setShowTransactionModal(true);
      setShowTransactionPrompt(false);
    } else {
      try {
        await coins.bulkUpdateStatus(Array.from(selectedCoins), pendingStatusUpdate);
        onUpdate();
        setSelectedCoins(new Set());
        setShowTransactionPrompt(false);
        setPendingStatusUpdate(null);
      } catch (err) {
        console.error('Failed to update coin statuses:', err);
        setError('Failed to update coin statuses. Please try again later.');
        setShowTransactionPrompt(false);
        setPendingStatusUpdate(null);
      }
    }
  };

  if (selectedCoins.size === 0) return null;

  return (
    <>
      <div className="fixed bottom-3 inset-x-0 z-50">
        <div className="flex justify-center">
          <div style={{ 
            backgroundColor: '#FFFFFF', 
            background: 'linear-gradient(rgba(79, 69, 225, 0.03), rgba(79, 69, 225, 0.03)), #FFFFFF',
            border: '1px solid rgba(79, 69, 225, 0.1)'
          }} className="rounded-lg shadow-xl mx-4">
            <div className="p-3 flex flex-wrap justify-center items-center gap-2">
              <span className="text-sm font-medium text-gray-700 mr-2">
                {selectedCoins.size} selected
              </span>
              <button
                onClick={handleSelectAll}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Select All
              </button>
              <button
                onClick={() => setSelectedCoins(new Set())}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Deselect
              </button>
              <button
                onClick={async () => {
                  try {
                    const statuses = await coins.getPossibleStatuses();
                    setPossibleStatuses(statuses);
                    setShowStatusUpdate(true);
                  } catch (err) {
                    console.error('Failed to fetch possible statuses:', err);
                    setError('Failed to load status options. Please try again later.');
                  }
                }}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                Edit Status
              </button>
              <button
                onClick={() => {
                  const selectedCoinData = coinList.filter(coin => selectedCoins.has(String(coin.id)));
                  
                  if (selectedCoinData.length === 0) {
                    setError('No coins selected');
                    return;
                  }

                  if (!selectedCoinData.some(coin => coin.certification_number || coin.pcgs_number)) {
                    setError('No certification numbers or PCGS numbers found in selected coins');
                    return;
                  }

                  document.dispatchEvent(new CustomEvent('openBulkCertUpdate', {
                    detail: { 
                      coins: selectedCoinData,
                      certNumbers: selectedCoinData
                        .map(coin => coin.certification_number || coin.pcgs_number)
                        .filter(Boolean)
                        .join('\n')
                    }
                  }));
                }}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Fetch Data
              </button>
              <button
                onClick={() => setShowBulkTagEditor(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Sets & Tags
              </button>
              <button
                onClick={() => setShowDocumentManager(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
              >
                Manage Docs
              </button>
              <button
                onClick={() => setShowDeleteWarning(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Warning Modal */}
      {showDeleteWarning && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Deletion</h3>
            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to delete {selectedCoins.size} selected coin{selectedCoins.size !== 1 ? 's' : ''}? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowDeleteWarning(false)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusUpdate && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full relative">
            <button
              onClick={() => setShowStatusUpdate(false)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-500"
            >
              <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span className="sr-only">Close</span>
            </button>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Update Status</h3>
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="block w-full px-3 py-2 mb-4 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="">Select new status...</option>
              {possibleStatuses.map(status => (
                <option 
                  key={typeof status === 'object' ? status.name : status} 
                  value={typeof status === 'object' ? status.name : status}
                  style={status.color ? { backgroundColor: status.color } : {}}
                >
                  {typeof status === 'object' ? status.name : status}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowStatusUpdate(false)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkStatusUpdate}
                disabled={!newStatus}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Prompt Modal */}
      {showTransactionPrompt && (
        <TransactionPromptModal
          onConfirm={handleConfirmTransaction}
          onCancel={() => {
            setShowTransactionPrompt(false);
            setPendingStatusUpdate(null);
            setError(null);
          }}
        />
      )}

      {/* Sale Transaction Modal */}
      {showTransactionModal && (
        <SaleTransactionModal
          isOpen={true}
          onClose={() => {
            setShowTransactionModal(false);
            setPendingStatusUpdate(null);
          }}
          onSubmit={async (transactionData) => {
            try {
              await transactions.create(transactionData);
              onUpdate();
              setSelectedCoins(new Set());
              setShowTransactionModal(false);
              setPendingStatusUpdate(null);
            } catch (err) {
              console.error('Failed to create transaction:', err);
              setError('Failed to create transaction. Please try again later.');
            }
          }}
          coins={coinList.filter(coin => selectedCoins.has(String(coin.id)))}
        />
      )}

      {/* Bulk Tag Editor Modal */}
      {showBulkTagEditor && (
        <BulkTagEditor
          isOpen={true}
          onClose={() => setShowBulkTagEditor(false)}
          selectedCoins={selectedCoins}
          onUpdate={() => {
            onUpdate();
            setSelectedCoins(new Set());
          }}
        />
      )}

      {/* Toast Error Display */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-lg z-50">
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      {/* Document Manager Modal */}
      {showDocumentManager && (
        <BulkDocumentManager
          isOpen={showDocumentManager}
          onClose={() => {
            setShowDocumentManager(false);
          }}
          selectedCoins={selectedCoins}
          onUpdate={onUpdate}
        />
      )}
    </>
  );
};

export default CoinListBulkActions;
